import Immutable from 'immutable';

import type { ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS } from 'js/src/libs/consts';

export const BY_ID = 'byId';
export const ALL_IDS = 'allIds';
export const ID_FIELD = '_id';
export const ID_PREFIX = 'idPrefix';

class IdGeneratorClass {
    prefix_counters: Record<string, number>;

    constructor() {
        this.prefix_counters = {};
    }

    generateId(prefix: string) {
        if (!Object.prototype.hasOwnProperty.call(this.prefix_counters, prefix)) {
            this.prefix_counters[prefix] = 0;
        }

        const this_id = `${prefix}-${this.prefix_counters[prefix]}`;
        this.prefix_counters[prefix]++;

        return this_id;
    }
}

export const IdGenerator = new IdGeneratorClass();

/**
# What is this
The StoreORM is a utility class for handling redux store data as if it was a "database".

# When to use
Only use the StoreORM if the data:
```
- should go in the redux store, AND
- is list based (there are potentially multiple rows of data)
```

# What is the ID Prefix
Each "database table" uses a unique id prefix, then each entry in the table will use
that id prefix to create unique ids for each new entry. For example, for the "sentences"
table, the id prefix is "sentence", and each sentence item will have an id field (the key
to the id field is _id) that looks like: sentence-0, sentence-1, sentence-2

# How to create
To create a new "database table", use the newFromIdPrefix static method
```
const orm = StoreORM.newFromIdPrefix<StateType, string>('sentence'); // creates the sentences table and an ORM
```

If there is existing state, you can create a StoreORM object for it:
```
const orm = new StoreORM<StateType, string>(existing_state); // creates an ORM to handle existing data
```

# How to save data
A redux reducer should return the underlying data so at the end of the reducer, you'll probably
want to:
```
return orm.data; // returns the data from the ORM to store in redux state
```

# How to read data
The most flexible method to search inside the "database table":
```
// get a list of entries in the list that match some condition:
const results = orm.query({
    key: ['sentence_text'],
    value: 'this is the sentence we are looking for.',
});
```

You can also use other methods, like getById, getByIndex, forEach, filter, map,
etc. to read data

# How to edit data
The most common way to update is by a specific id:
```
// update sentence 2:
orm.updateById('sentence-2', {
    key: ['sentence_text'],
    value: 'this is the new sentence text.',
});
```

A common pattern would be to `query()` for some condition, and then `updateById`
all the results returned by the query.

# How to add new data
It's easy to add new data:

```
orm.pushJSItem({
    sentence_text: 'this is a new sentence',
});
```

# How to remove data
You can delete data like this:
```
orm.delete(['sentence-0', 'sentence-1']);
```

# Normal usage
Normally, this is how a reducer will use the `StoreORM`:

```
function reducer(state: RootState) {
    const orm = new StoreORM<StateType>(state);
    // do operations using the orm
    return orm.data;
}
```

# How data is stored
Internally, the StoreORM stores data like this (but all immutable):

```
{
    byId: {
        'sentence-0': {
            '_id': 'sentence-0'
            <rest of sentence-0 data>
        },
        ...
    },
    allIds: [
        'sentence-0',
        ...
    ],
    idPrefix: 'sentence',
}
```

That structure is useful because:
```
- it's flat, which means faster updates and accesses
- it's fast to get an item by ID
- it can be relational (an object can store an ID of another object to reference that object)
```

For documentation on a specific method, see the method!

@class
*/

export type StoreORMInterface<
    StoreRecord extends object,
    IDType extends number | string = number,
> = ImmutableMap<{
    byId: ImmutableMap<Record<IDType, StoreRecord>>;
    allIds: Immutable.List<IDType>;
    idPrefix: string;
}>;
export enum OrderDirection {
    asc = 'asc',
    desc = 'desc',
}
export enum Evaluator {
    equals = '===',
    not_equals = '!==',
}
type Query = {
    key: (string | number)[]; // the array to pass to getIn inside each item
    value: unknown; // the value to check against
    evaluator?: Evaluator; // default = '==='
    orderBy?: {
        // An object describing how ordering of results should work
        key: string[];
        direction?: OrderDirection; // (default = 'asc')
    };
    removeFromList?: boolean; // if the value is something to remove from a list of values
    store_function?: boolean; // if true, will store the query.value even if it is a function
};

export default class StoreORM<StoreRecord extends object, IDType extends number | string = number> {
    /**
        Create an ORM for existing data.
    */
    store: StoreORMInterface<StoreRecord, IDType>;

    constructor(store: StoreORMInterface<StoreRecord, IDType>) {
        this.store = store;
    }

    /**
        Create a new "database table" and an ORM for it
        @param {string} id_prefix - the string to prefix all if the ids in this "table"
    */
    static newFromIdPrefix<StoreRecord extends object, IDType extends number | string = number>(
        id_prefix: string,
    ) {
        const shape = {
            [BY_ID]: ImmutableFromJS({}) as unknown as ImmutableMap<Record<IDType, StoreRecord>>,
            [ALL_IDS]: Immutable.List<IDType>(),
            [ID_PREFIX]: id_prefix,
        };
        const store = ImmutableFromJS(shape);
        return new StoreORM<StoreRecord, IDType>(store);
    }

    hasId(id: IDType): boolean {
        return this.store.hasIn([BY_ID, id]);
    }

    hasIndex(index: number): boolean {
        return this.store.hasIn([ALL_IDS, index]);
    }

    /**
     * Get an entry by id or throw an error if id does not exist.
     */
    getById(id: IDType): ImmutableMap<StoreRecord> {
        if (this.hasId(id)) {
            return this.store.getIn([BY_ID, id]) as ImmutableMap<StoreRecord>;
        }
        throw new Error('getById: no entry exists. Did you mean to call getByIdOrUndefined?');
    }

    /**
     * Get an entry by id or undefined if id does not exist.
     * Use when it is expected that id will not exist.
     */
    getByIdOrUndefined(id: IDType): ImmutableMap<StoreRecord> | undefined {
        try {
            return this.getById(id);
        } catch (e) {
            return undefined;
        }
    }

    /**
     * Get the id for an entry located at an index or throw error if index does not exist.
     */
    getIdByIndex(index: number): IDType {
        if (this.hasIndex(index)) {
            return this.store.getIn([ALL_IDS, index]) as IDType;
        }
        throw new Error(`getIdByIndex: no id exists for index=${index}`);
    }

    /**
     * Get the id for an entry located at an index or undefined if index does not exist.
     * Use when it is expected that index will not exist.
     */
    getIdByIndexOrUndefined(index: number): IDType | undefined {
        try {
            return this.getIdByIndex(index);
        } catch (e) {
            return undefined;
        }
    }

    /**
     * Get an entry by index or throw error if index does not exist.
     */
    getByIndex(index: number): ImmutableMap<StoreRecord> {
        return this.getById(this.getIdByIndex(index));
    }

    /**
     * Get an entry by index or undefined if index does not exist.
     * Use when it is expected that index will not exist.
     */
    getByIndexOrUndefined(index: number): ImmutableMap<StoreRecord> | undefined {
        try {
            return this.getByIndex(index);
        } catch (e) {
            return undefined;
        }
    }

    /**
        @return {Immutable.List} A list of all the ids (in order, if order is important)
    */
    getAllIds(): Immutable.List<IDType> {
        return this.store.get(ALL_IDS);
    }

    /**
        Edit each entry in the store
    */
    updateAll(query: Query) {
        this.store.get(ALL_IDS).forEach((id) => {
            this.updateById(id, query);
        });
    }

    /**
        Edit a specific entry in the "table", referenced by id
    */
    updateById(id: IDType, query: Query) {
        if (this.contains(id)) {
            const update_path = ([BY_ID, id] as (string | number)[]).concat(query.key);
            const update_value = this._evaluate_value(query, update_path);

            if (query.removeFromList === true) {
                const item_index = (this.store.getIn(update_path) as Immutable.List<any>).indexOf(
                    update_value,
                );
                if (item_index >= 0) {
                    this.store = this.store.setIn(
                        update_path,
                        (this.store.getIn(update_path) as Immutable.List<any>).delete(item_index),
                    );
                }
            } else {
                this.store = this.store.setIn(update_path, update_value);
            }
        }
    }

    /**
        Remove a set of entries from the table by id
        @param id_iterable - a series of id strings that identify entries that should be removed from the table
    */
    delete(id_iterable: IDType[] | Immutable.List<IDType>) {
        if (this.containsOneOf(id_iterable)) {
            // delete the ids from the id list
            this.store = this.store.update(ALL_IDS, (id_list) => {
                return id_list.filter((id) => {
                    return id_iterable.indexOf(id) === -1;
                });
            });

            // delete the values
            id_iterable.forEach((id) => {
                this.store = this.store.deleteIn([BY_ID, id]);
            });
        }
    }

    /**
        Queries the store for items matching the query passed in.
    */
    query(query: Query): Immutable.List<ImmutableMap<StoreRecord>> {
        const evaluator = query.evaluator ? query.evaluator : Evaluator.equals;

        let filtered = this.filter((object) => {
            if (evaluator === Evaluator.equals) {
                return object.getIn(query.key) === query.value;
            } else if (evaluator === Evaluator.not_equals) {
                return object.getIn(query.key) !== query.value;
            } else {
                throw Error('unknown evaluator');
            }
        });

        const orderBy = query.orderBy;
        if (orderBy) {
            const descending = orderBy.direction === OrderDirection.desc;
            filtered = filtered.sort(
                (item_one: ImmutableMap<StoreRecord>, item_two: ImmutableMap<StoreRecord>) => {
                    const item_one_val = item_one.getIn(orderBy.key) as any;
                    const item_two_val = item_two.getIn(orderBy.key) as any;
                    if (descending) {
                        return item_two_val - item_one_val;
                    } else {
                        return item_one_val - item_two_val;
                    }
                },
            );
        }

        return filtered;
    }

    queryIds(query: Query): Immutable.List<IDType> {
        return this.query(query).map((item) => item.get(ID_FIELD as any) as IDType);
    }

    /**
        Add an entry into the table
        @param {Object} item - A plain javascript object to push into the table.
        @param {} id_field - Field value from the object to consider for id value.
        An id will automatically be added to the object, if it's not provided .
        @return the id of the newly added entry
    */
    pushJSItem(item: StoreRecord, id_field?: string): IDType {
        return this.pushItem(ImmutableFromJS(item) as ImmutableMap<StoreRecord>, id_field);
    }

    /**
        Add an entry into the table
        @param {Object} item - A plain javascript object to push into the table.
        @param {} id_field - Field value from the object to consider for id value.
        An id will automatically be added to the object, if it's not provided .
        @return the id of the newly added entry
    */
    pushJSItemToFront(item: StoreRecord, id_field?: string): IDType {
        return this.pushItemToFront(ImmutableFromJS(item) as ImmutableMap<StoreRecord>, id_field);
    }

    /**
        Add an entry into the table
        @param {Immutable.Map} item - An immutable object to push into the table.
         @param {} id_field - Field value from the object to consider for id value.
        An id will automatically be added to the object, if it's not provided.
        @return {string} the id of the newly added entry
    */
    pushItem(item: ImmutableMap<StoreRecord>, id_field?: string): IDType {
        let id: IDType;
        if (id_field !== undefined && item.get(id_field as keyof StoreRecord) !== undefined) {
            id = item.get(id_field as keyof StoreRecord) as IDType;
        } else {
            id = IdGenerator.generateId(this.store.get(ID_PREFIX)) as IDType; // TODO: if IDType is number throw error?
            item = item.set(ID_FIELD as keyof StoreRecord, id as any);
        }
        this.store = this.store.setIn([BY_ID, id], item).update(ALL_IDS, (id_list) => {
            if (!id_list.includes(id)) {
                id_list = id_list.push(id);
            }
            return id_list;
        });
        return id;
    }

    /**
        Add an entry into the table, the id will be added to the front of the ALL_IDS list
        @param {Immutable.Map} item - An immutable object to push into the table.
         @param {} id_field - Field value from the object to consider for id value.
        An id will automatically be added to the object, if it's not provided.
        @return {string} the id of the newly added entry
    */
    pushItemToFront(item: ImmutableMap<StoreRecord>, id_field?: string) {
        let id: IDType;
        if (id_field !== undefined && item.get(id_field as keyof StoreRecord) !== undefined) {
            id = item.get(id_field as keyof StoreRecord) as IDType;
        } else {
            id = IdGenerator.generateId(this.store.get(ID_PREFIX)) as IDType; // TODO: if IDType is number throw error?
            item = item.set(ID_FIELD as keyof StoreRecord, id as any);
        }
        this.store = this.store.setIn([BY_ID, id], item).update(ALL_IDS, (id_list) => {
            id_list = id_list.filter((item) => item !== id);
            return this._ensure_is_immutable_list([id, ...id_list]);
        });
        return id;
    }

    /**
        a `forEach` method for a subset of the table, as specified by `id_iterable`. This
        is a synchronous method.

        @param {Array|Immutable.List} id_iterable - a set of ids to iterate over
        @param {function} callback - callback that accepts the entry and the index

        @example
        * orm.forEachByIds(['sentence-0', 'sentence-1'], (item, index) => {
        *     console.log(index, item.get('sentence_content'));
        * });
    */
    forEachByIds(
        id_iterable: IDType[] | Immutable.List<IDType>,
        callback: (entry: ImmutableMap<StoreRecord>, index: number) => void,
    ) {
        return id_iterable.forEach((id, index) => {
            return callback(this.getById(id), index);
        });
    }

    /**
        a `map` method for a subset of the table, as specified by `id_iterable`. This is
        a synchronous method.

        @param {Array|Immutable.List} id_iterable - a set of ids to iterate over
        @param {function} callback - callback that accepts the entry and the index
        @return {Immutable.List} the set of results from the callback

        @example
        * const content_list = orm.mapByIds(['sentence-0', 'sentence-2'], (item, index) => {
        *     return item.get('sentence_content');
        * });
    */
    mapByIds<U>(
        id_iterable: IDType[] | Immutable.List<IDType>,
        callback: (item: ImmutableMap<StoreRecord>, index: number) => U,
    ): Immutable.List<U> {
        const result = id_iterable.map((id, index) => {
            return callback(this.getById(id), index);
        });

        return this._ensure_is_immutable_list(result);
    }

    /**
        A `filter` method for a subset of the table, as specified by `id_iterable`.
        This is a synchronous method.

        @param {Array|Immutable.List} id_iterable - a set of ids to iterate over
        @param {function} callback - callback that accepts the entry and the index
        @return {Immutable.List} the set of items that were not filtered out

        @example
        * const long_sentences = orm.filterByIds(['sentence-0', 'sentence-4'], (item, index) => {
        *     return item.get('sentence_content').length > 99;
        * });
    */
    filterByIds(
        id_iterable: Immutable.List<IDType>,
        callback: (entry: ImmutableMap<StoreRecord>, index: number) => boolean,
    ): Immutable.List<ImmutableMap<StoreRecord>> {
        const ids = id_iterable.filter((id, index) => {
            const object = this.getById(id);
            return callback(object, index);
        });

        const result = ids.map((id) => this.getById(id));

        return this._ensure_is_immutable_list(result);
    }

    /**
        A `filter` method for the entire table.

        @param {function} callback - callback that accepts the entry and the index
        @return {Immutable.List} the set of items that were not filtered out

        @example
        * const long_sentences = orm.filter((item, index) => {
        *     return item.get('sentence_content').length > 99;
        * });
    */
    filter(
        callback: (entry: ImmutableMap<StoreRecord>, index: number) => boolean,
    ): Immutable.List<ImmutableMap<StoreRecord>> {
        return this.filterByIds(this.store.get(ALL_IDS), callback);
    }

    /**
     * A `find` method for the entire table.
     *
     * @param {function} callback - callback that accepts the entry and the index
     * @returns {Immutable.Map} the object that matched
     */
    find(
        callback: (entry: ImmutableMap<StoreRecord>, index: number) => boolean,
    ): ImmutableMap<StoreRecord> | undefined {
        const filtered = this.filter(callback);
        if (filtered.size > 0) {
            return filtered.get(0);
        }
        return undefined;
    }

    /**
        A `forEach` method for the entire table.

        @param {function} callback - callback that accepts the entry and the index

        @example
        * orm.forEach((item, index) => {
        *     console.log(index, item.get('sentence_content'));
        * });
    */
    forEach(callback: (entry: ImmutableMap<StoreRecord>, index: number) => void) {
        this.forEachByIds(this.store.get(ALL_IDS), callback);
    }

    /**
        A `map` method for the entire table.
        @param {function} callback - callback that accepts the entry and the index
        @returns {Immutable.List} the set of results from the callback

        @example
        * const content_list = orm.map((item, index) => {
        *     return item.get('sentence_content');
        * });
    */
    map<U>(callback: (item: ImmutableMap<StoreRecord>, index: number) => U): Immutable.List<U> {
        return this.mapByIds(this.store.get(ALL_IDS), callback);
    }

    /**
        @return {boolean} if the table has 0 entries
    */
    isEmpty(): boolean {
        return this.length === 0;
    }

    /**
        @param {Array|Immutable.List} id_iterable - a set of ids to check
        @return {boolean} true if an entry for one of the ids in `id_iterable` exists in the table.
    */
    containsOneOf(id_iterable: IDType[] | Immutable.List<IDType>): boolean {
        const BreakException = {};

        try {
            id_iterable.forEach((id) => {
                if (this.contains(id)) {
                    throw BreakException;
                }
            });
        } catch (e) {
            if (e === BreakException) {
                return true;
            } else {
                throw e;
            }
        }
        return false;
    }

    /**
        @param {string} id - a specific id
        @return {boolean} if an entry for the id exists in the table.
    */
    contains(id: IDType): boolean {
        return this.store.getIn([BY_ID, id]) !== undefined;
    }

    /**
        @param {string} id - a specific id
        @return {Number} the index into the list where the id exists
        or -1 if it doesn't exist
    */
    indexOf(id: IDType) {
        return this.store.get(ALL_IDS).indexOf(id);
    }

    _evaluate_value(query: Query, path: any) {
        let value = query.value;
        if (query.store_function !== true && typeof value === 'function') {
            value = value(this.store.getIn(path));
        }
        return value;
    }

    _ensure_is_immutable_list<Generic>(
        list: Immutable.List<Generic> | Generic[],
    ): Immutable.List<Generic> {
        if (!Immutable.List.isList(list)) {
            return Immutable.List(list);
        }
        return list;
    }

    /**
        @return {number} the number of entries in the table
    */
    get length(): number {
        return this.store.get(ALL_IDS).size;
    }

    /**
        @return {Immutable.Map} all the data to store in the redux store
    */
    get data(): StoreORMInterface<StoreRecord, IDType> {
        return this.store;
    }
}
